import { createContext, useContext, useMemo } from "react";
import { useLocation, matchPath } from "react-router-dom";

export const SUPPORTED_LOCALES = ["fr", "nl", "en"];
export const DEFAULT_LOCALE = "fr";
export const LOCALE_LABELS = { fr: "Français", nl: "Nederlands", en: "English" };
export const LOCALE_SHORT = { fr: "FR", nl: "NL", en: "EN" };

const LanguageContext = createContext({ locale: DEFAULT_LOCALE, basePath: "" });

/** Parse the current locale from the URL. `/` = fr, `/nl/...` = nl, `/en/...` = en. */
export function LanguageProvider({ children }) {
    const location = useLocation();
    const value = useMemo(() => {
        const pathname = location.pathname;
        const match = matchPath({ path: "/:lang/*", end: false }, pathname);
        const first = match?.params?.lang;
        if (first && SUPPORTED_LOCALES.includes(first) && first !== DEFAULT_LOCALE) {
            return { locale: first, basePath: `/${first}` };
        }
        return { locale: DEFAULT_LOCALE, basePath: "" };
    }, [location.pathname]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    return useContext(LanguageContext);
}

/** Build a path under the current locale prefix. `/matelas` → `/nl/matelas`. */
export function withLocalePrefix(path, locale) {
    if (!locale || locale === DEFAULT_LOCALE) return path.startsWith("/") ? path : `/${path}`;
    const clean = path.startsWith("/") ? path : `/${path}`;
    if (clean === "/") return `/${locale}`;
    return `/${locale}${clean}`;
}

/** Return the same page in a different locale. */
export function switchLocale(currentPath, newLocale) {
    // strip existing locale prefix
    const match = matchPath({ path: "/:lang/*", end: false }, currentPath);
    let core = currentPath;
    if (match && SUPPORTED_LOCALES.includes(match.params.lang) && match.params.lang !== DEFAULT_LOCALE) {
        core = currentPath.replace(new RegExp(`^/${match.params.lang}`), "") || "/";
    }
    return withLocalePrefix(core, newLocale);
}

/** Content i18n micro-dictionary for static UI labels (nav, buttons, form) */
export const UI_STRINGS = {
    fr: {
        nav_interior: "Votre intérieur",
        nav_realisations: "Réalisations",
        nav_matelas: "Matelas",
        nav_know_how: "Notre savoir faire",
        nav_process: "Process",
        nav_contact: "Contact",
        cta_quote: "Demander un devis",
        loading: "Chargement…",
        studio_overline: "Studio d'aménagement · Etterbeek",
        gallery_all: "Tout",
    },
    nl: {
        nav_interior: "Uw interieur",
        nav_realisations: "Realisaties",
        nav_matelas: "Matrassen",
        nav_know_how: "Onze vakkennis",
        nav_process: "Werkwijze",
        nav_contact: "Contact",
        cta_quote: "Vraag een offerte",
        loading: "Laden…",
        studio_overline: "Interieurstudio · Etterbeek",
        gallery_all: "Alle",
    },
    en: {
        nav_interior: "Your interior",
        nav_realisations: "Projects",
        nav_matelas: "Mattresses",
        nav_know_how: "Our craft",
        nav_process: "Process",
        nav_contact: "Contact",
        cta_quote: "Request a quote",
        loading: "Loading…",
        studio_overline: "Interior studio · Etterbeek",
        gallery_all: "All",
    },
};

export function ui(locale, key) {
    return (UI_STRINGS[locale] && UI_STRINGS[locale][key]) || UI_STRINGS[DEFAULT_LOCALE][key] || key;
}
